"use client";
import { useEffect } from "react";
import { Track } from "livekit-client";
import { toastNotify } from "@client/index";
import { useTranslations } from "next-intl";
import { useTrackToggle } from "@livekit/components-react";

/**
 * Keyboard shortcuts handler.
 * - Ctrl/Cmd + D: toggle microphone
 * - Ctrl/Cmd + E: toggle camera
 */
interface KeyboardShortcutsProps {
  onToggleChat?: () => void;
  onToggleParticipants?: () => void;
  onToggleHandRaise?: () => void;
}

/**
 * Keyboard shortcuts handler.
 * - Ctrl/Cmd + D: toggle microphone
 * - Ctrl/Cmd + E: toggle camera
 * - Ctrl + Alt + H: toggle hand raise
 * - Ctrl + Alt + C: toggle chat sidebar
 * - Ctrl + Alt + P: toggle participants sidebar
 */
export const KeyboardShortcuts = ({
  onToggleChat,
  onToggleParticipants,
  onToggleHandRaise
}: KeyboardShortcutsProps) => {
  const t = useTranslations();
  const { toggle: toggleMic, buttonProps: _micProps } = useTrackToggle({
    source: Track.Source.Microphone
  });

  const { toggle: toggleCamera, buttonProps: _camProps } = useTrackToggle({
    source: Track.Source.Camera
  });

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      /** Toggle microphone: Cmd/Ctrl + D */
      if (event.key.toLowerCase() === "d" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleMic?.();
        toastNotify.info(t("toast.info.micToggled"));
      }

      /** Toggle camera: Cmd/Ctrl + E */
      if (event.key.toLowerCase() === "e" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleCamera?.();
        toastNotify.info(t("toast.info.cameraToggled"));
      }

      /** Toggle Hand Raise: Ctrl + Alt + H */
      if (event.key.toLowerCase() === "h" && (event.ctrlKey || event.metaKey) && event.altKey) {
        event.preventDefault();
        onToggleHandRaise?.();
      }

      /** Toggle Chat: Ctrl + Alt + C */
      if (event.key.toLowerCase() === "c" && (event.ctrlKey || event.metaKey) && event.altKey) {
        event.preventDefault();
        onToggleChat?.();
      }

      /** Toggle Participants: Ctrl + Alt + P */
      if (event.key.toLowerCase() === "p" && (event.ctrlKey || event.metaKey) && event.altKey) {
        event.preventDefault();
        onToggleParticipants?.();
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [toggleMic, toggleCamera, onToggleChat, onToggleParticipants, onToggleHandRaise, t]);

  return null;
};
